import { useEffect, useState } from 'react';
import { Route } from 'react-router-dom';
import { HomePage } from './components/home/HomePage';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { ExplorePage } from '@backstage/plugin-explore';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, keycloakAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { GitlabPage } from './components/gitlab/GitlabPage';
import { AlertRulesPage } from './components/admin/AlertRulesPage';
import { GcpCostPage } from './components/admin/GcpCostPage';
import { useGcpCostEnabled } from './hooks/useGcpCostEnabled';
// AlertCenterPage merged into AlertRulesPage as tab

import { AlertDisplay, ErrorPage, SignInPage } from '@backstage/core-components';
import type { SignInPageProps } from '@backstage/core-plugin-api';
import { SessionRefreshDialog } from './components/SessionRefreshDialog';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { GitlabRepoPathFieldExtension } from './components/scaffolder/extensions';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';

type ArcusSignInProps = Omit<SignInPageProps, 'provider'>;

const ArcusSignInPage = (props: ArcusSignInProps) => {
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('error')) {
      url.searchParams.delete('error');
      window.history.replaceState(null, '', url.toString());
    }
    // auto 리다이렉트 실패 시 3초 후 fallback 버튼 표시
    const timer = setTimeout(() => setShowFallback(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0a1628 0%, #0d3b66 50%, #0a1628 100%)',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{ display: showFallback ? 'block' : 'none' }}>
        <SignInPage
          {...props}
          auto
          provider={{
            id: 'keycloak',
            title: 'Sign in with Arcus SSO',
            message: 'Use your Arcus Keycloak account.',
            apiRef: keycloakAuthApiRef,
          }}
        />
      </div>
      {!showFallback && (
        <div style={{ textAlign: 'center', color: '#fff', fontFamily: 'Poppins, sans-serif' }}>
          <div style={{ fontSize: '14px', opacity: 0.7 }}>Redirecting to login...</div>
        </div>
      )}
    </div>
  );
};

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => <ArcusSignInPage {...props} />,
  },
});

/**
 * ★ D27 (2026-09-21): `arcus.features.gcpCost` 가 false 면 `/admin/gcp-cost` 직접 진입도 404 화면.
 *   (온프렘 = 백엔드 gcp-cost 라우터 미등록 = 15-b · 사이드바 링크는 Root 에서 제거.) GCP = 플래그 기본 true → 종전 그대로.
 */
const GcpCostRouteGate = ({ children }: { children: JSX.Element }) => {
  const enabled = useGcpCostEnabled();
  if (!enabled) {
    return <ErrorPage status="404" statusMessage="GCP Cost 기능이 이 환경에서는 비활성입니다" />;
  }
  return children;
};

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    {/* title prop은 현 버전 런타임이 무시(i18n 사용) — 타입 오류만 내는 죽은 prop이라 제거 */}
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          components={{ FieldExtensions: [GitlabRepoPathFieldExtension] }}
        />
      }
    />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route
      path="/gitlab"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <GitlabPage />
        </RequirePermission>
      }
    />
    <Route path="/explore" element={<ExplorePage />} />
    <Route path="/announcements" element={<AnnouncementsPage defaultInactive={false} />} />
    <Route
      path="/admin/alert-rules"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <AlertRulesPage />
        </RequirePermission>
      }
    />
    {/* Alert Center merged into AlertRulesPage as tab */}
    <Route
      path="/admin/gcp-cost"
      element={
        <GcpCostRouteGate>
          <RequirePermission permission={catalogEntityCreatePermission}>
            <GcpCostPage />
          </RequirePermission>
        </GcpCostRouteGate>
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <SessionRefreshDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
